/**
 * DEFAULT_BRAND — Papan, compiled in.
 *
 * The bottom layer of every resolution, and what an unconfigured deployment
 * renders in full. EVERY VALUE HERE REPRODUCES TODAY'S SHIPPED LOOK, so
 * changing one is a visible product change, not a refactor.
 */
import { BRAND_SCHEMA_VERSION, type BrandSnapshot } from './snapshot.js'

/**
 * The canonical brand purple, kept as an exported constant because the SPA's
 * stylesheet and the marketing site both encode it and a drift between them
 * has bitten before.
 *
 * No longer a brand TOKEN: colours are not configurable, so this is the
 * product's accent rather than a per-brand value. `--brand-300…700` in
 * apps/web/src/index.css are the live definition; this is documentation with
 * a type.
 */
export const CANONICAL_BRAND_PURPLE = '#a855f7'

/** The canonical gradient end (Tailwind blue-500) — the other half of the
 *  logo/CTA gradient this codebase leans on heavily. */
export const CANONICAL_BRAND_BLUE = '#3b82f6'

export const DEFAULT_BRAND: BrandSnapshot = {
  schemaVersion: BRAND_SCHEMA_VERSION,
  brandKey: 'papan',
  identity: {
    productName: 'Papan',
    tagline: 'Any file is a database',
    // Papan's own legal entity is deliberately unset — the render sites fall
    // back to productName, which is what they display today.
    rootTitleTemplate: '{product} - {tagline}',
    pageTitleTemplate: '{page} · {product}',
    assistantName: 'Papan AI',
  },
  assets: {
    // Matches the file the SPA already serves at its web root
    // (apps/web/public/favicon.svg). Every other asset but `appIcon` below is intentionally unset
    // so each render site keeps using its existing inline default SVG.
    favicon: '/favicon.svg',
    // The home-screen icon the SPA already serves (apps/web/public/icons/),
    // used by both the apple-touch-icon link and the web app manifest.
    appIcon: '/icons/apple-touch-icon.png',
  },
  legal: {
    // Root-relative, so it follows the deployment rather than the vendor: every
    // brand serves the SPA on its own host, and `/privacy` there renders that
    // brand's own company. A customer whose policy lives on their marketing
    // site overrides this with an absolute URL.
    privacyUrl: '/privacy',
    // termsUrl deliberately unset — no terms page exists yet, and an unset slot
    // renders no link rather than a dead one.
  },
  experience: {
    // Papan lands on the chat home, which is what every deployment did before
    // this key existed. A brand opts INTO the launcher; nothing opts out of it
    // by accident.
    //
    // The LAST link of the chain, not the only one:
    // `platform_settings.landing_experience → brand.yaml → this`. See
    // `BrandExperience` in snapshot.ts.
    landing: 'chat',
  },
}
